import { apiFetch } from "./client";
import type { ContractErpTerms } from "./contracts";
import type { FilterOption, Page } from "./admin";
import type { CompanyAddress, CompanyAddressBody } from "./admin";

export type OrderStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "IN_REVIEW"
  | "APPROVED"
  | "REJECTED"
  | "SHIPPED"
  | "INVOICED"
  | "CANCELLED";

/** One point on the order's status timeline. `changedByName` is null for system rows. */
export type StatusHistoryEntry = {
  status: OrderStatus;
  changedAt: string;
  changedByName: string | null;
};

export type OrderLine = {
  id: string;
  position: number;
  productId: string | null;
  productSku: string | null;
  productName: string | null;
  freeTextDescription: string | null;
  quantity: number;
  unit: string;
  customerNotes: string | null;
  catalogUnitPrice: number | null;
  currency: string | null;
  contractedDiscountPct: number;
  finalDiscountPct: number;
  discountReason: string | null;
  netUnitPrice: number | null;
  lineTotal: number | null;
};

/** Frozen address snapshot shown on the order/invoice. `recipientName` is null
 * for billing-only views; `vat` is null for delivery-only views. */
export type OrderAddress = {
  name: string | null;
  recipientName: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  postal: string | null;
  country: string | null;
  vat: string | null;
};

export type Order = {
  id: string;
  companyId: string;
  companyName: string;
  companyCountry: string | null;
  orderNumber: string;
  status: OrderStatus;
  requestedDeliveryDate: string | null;
  customerNotes: string | null;
  salesNotes: string | null;
  customerReference: string | null;
  createdBy: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: OrderLine[];
  orderTotal: number;
  currency: string;
  statusHistory: StatusHistoryEntry[];
  /** Frozen delivery address snapshot, or null when none captured. */
  deliveryAddress: OrderAddress | null;
  /** Frozen billing address snapshot, or null when none captured. */
  billingAddress: OrderAddress | null;
  /** True for an order carried over from the ERP: the portal shows it, the ERP keeps it. */
  fromErp?: boolean;
  /** Bankom's terms of the ERP pro forma invoice the order becomes; absent for a customer. */
  incoterm?: string | null;
  incotermPlace?: string | null;
  paymentTerms?: string | null;
  transportAmount?: number | null;
  /** Who pays the transport: BANKOM (its own cost) or CUSTOMER (a line of the ERP pro forma invoice). */
  transportPaidBy?: "BANKOM" | "CUSTOMER" | null;
  /** Number of the ERP pro forma invoice an approved order became; null until it is written. */
  erpProformaNumber?: string | null;
  /** The ERP customer's order made from the pro forma invoice, with deferred payment; null otherwise. */
  erpCustomerOrderNumber?: string | null;
  /** Broj zahteva (ORD-…) koji je porudžbina nosila pre ERP-a; od tada je `orderNumber` ERP-ov broj. */
  portalNumber?: string | null;
};

/**
 * The ERP pro forma invoice an approved order became, as the ERP holds it now: the customer's own document. Amounts
 * are in its currency; `vat` is 0 on a foreign one, an export.
 */
export type OrderProforma = {
  number: string;
  issuedOn: string | null;
  validUntil: string | null;
  domestic: boolean;
  currency: string | null;
  /** The ERP's own name of the payment terms. */
  paymentTerms: string | null;
  /** Paid in advance, in full or in part: the customer pays by this document. */
  inAdvance: boolean;
  incoterm: string | null;
  deliveryTerm: string | null;
  note: string | null;
  customerName: string | null;
  customerAddress: string[];
  customerVatNumber: string | null;
  lines: {
    article: string;
    name: string | null;
    unit: string | null;
    quantity: number;
    unitPrice: number;
    discountPct: number;
    amount: number;
    vatPct: number;
  }[];
  net: number;
  vat: number;
  total: number;
  /** Where to pay, one account per line. */
  payTo: string[];
  /** What the payment names the document by. */
  paymentReference: string;
};

/** Bankom's terms of an order: an empty text clears one, and a transport of 0 means none. */
export type OrderErpTermsBody = {
  incoterm: string;
  incotermPlace: string;
  paymentTerms: string;
  transportAmount: number;
  transportPaidBy?: "BANKOM" | "CUSTOMER";
};

export type QuoteLine = {
  productId: string;
  quantity: number;
  catalogUnitPrice: number | null;
  contractedDiscountPct: number | null;
  netUnitPrice: number | null;
  lineTotal: number | null;
};

export type QuoteResult = {
  lines: QuoteLine[];
  orderTotal: number;
  currency: string;
  showDiscount: boolean;
};

export type LineInput = {
  productId?: string | null;
  freeTextDescription?: string | null;
  quantity: number;
  unit: string;
  customerNotes?: string | null;
};

const q = (params: Record<string, string | number | undefined>) => {
  const usp = new URLSearchParams();
  // 0 je valjana strana, pa se ne sme odbaciti kao prazno.
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") usp.set(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const ordersApi = {
  /**
   * Stranica porudžbina. Filteri i brojanje idu u bazu: sa istorijom iz starog sistema
   * spisak ima četrdeset hiljada redova, a svaki nosi i svoje stavke.
   */
  list: (params: {
    companyId?: string;
    status?: OrderStatus;
    country?: string;
    dateFrom?: string;
    dateTo?: string;
    q?: string;
    sort?: string;
    page?: number;
    size?: number;
  } = {}) =>
    apiFetch<Page<Order>>(`/orders${q({ size: 50, ...params })}`),
  /**
   * Brojači i zbirovi po statusu sa servera, nad istim skupom koji spisak prikazuje.
   *
   * Filtri se šalju da se zaglavlje i kartice ne bi razišli: bez njih zaglavlje gleda ceo skup
   * a kolona filtrirani, pa se pri promeni filtera kartice menjaju a brojevi ne.
   */
  counts: (params: {
    companyId?: string;
    country?: string;
    dateFrom?: string;
    dateTo?: string;
    q?: string;
  } = {}) =>
    apiFetch<{
      counts: Record<string, number>;
      /** Zbir po statusu i valuti, kako stoji na stavkama. */
      amounts: Record<string, Record<string, number>>;
      /** Isti zbir preračunat u evre, po kursu na dan porudžbine. */
      eur: Record<string, number>;
      /**
       * The two figures the status grouping cannot carry, because both need a date comparison:
       * shipped in the last thirty days, and late. They come from the server over the same visible
       * set as the list — computing them from the loaded page counts the fifty rows that page
       * happened to bring.
       */
      indicators: { shipped30: number; late: number };
      /** Per status, orders in a currency without any exchange rate: not in the euro sum. */
      noRate?: Record<string, number>;
    }>(`/orders/counts${q(params)}`),
  /** Države koje se u spisku stvarno pojavljuju, za padajući spisak. */
  countries: () => apiFetch<FilterOption[]>("/orders/countries"),
  get: (id: string) => apiFetch<Order>(`/orders/${id}`),
  proforma: (id: string) => apiFetch<OrderProforma>(`/orders/${id}/proforma`),
  create: (body: {
    companyId: string;
    requestedDeliveryDate?: string;
    customerNotes?: string;
    customerReference?: string;
    lines?: LineInput[];
    deliveryAddressId?: string | null;
    billingAddressId?: string | null;
  }) => apiFetch<Order>("/orders", { method: "POST", body }),
  update: (
    id: string,
    body: Partial<Pick<Order, "requestedDeliveryDate" | "customerNotes" | "salesNotes" | "customerReference">> & {
      deliveryAddressId?: string | null;
      billingAddressId?: string | null;
    } & Partial<OrderErpTermsBody>,
  ) => apiFetch<Order>(`/orders/${id}`, { method: "PUT", body }),
  /** The ERP's incoterms, payment terms and places of delivery an order picks its terms from. */
  erpTerms: (companyId?: string) => apiFetch<ContractErpTerms>(`/orders/erp-terms${q({ companyId })}`),
  // Customer-reachable address book for the order pickers — gated by company
  // access (own company / sales-assigned / admin), not companies:edit.
  listCompanyAddresses: (companyId: string) =>
    apiFetch<CompanyAddress[]>(`/orders/companies/${companyId}/addresses`),
  addCompanyAddress: (companyId: string, body: CompanyAddressBody) =>
    apiFetch<CompanyAddress>(`/orders/companies/${companyId}/addresses`, {
      method: "POST",
      body,
    }),
  quote: (companyId: string, lines: { productId?: string | null; quantity: number }[]) =>
    apiFetch<QuoteResult>("/orders/quote", { method: "POST", body: { companyId, lines } }),
  addLine: (id: string, body: LineInput) =>
    apiFetch<Order>(`/orders/${id}/lines`, { method: "POST", body }),
  removeLine: (id: string, lineId: string) =>
    apiFetch<Order>(`/orders/${id}/lines/${lineId}`, { method: "DELETE" }),
  submit: (id: string) => apiFetch<Order>(`/orders/${id}/submit`, { method: "POST" }),
  transition: (id: string, to: OrderStatus, salesNotes?: string) =>
    apiFetch<Order>(`/orders/${id}/transition`, { method: "POST", body: { to, salesNotes } }),
  applyPricing: (
    id: string,
    lines: { lineId: string; finalDiscountPct: number; reason?: string | null }[],
  ) => apiFetch<Order>(`/orders/${id}/pricing`, { method: "PUT", body: { lines } }),
  clone: (id: string) => apiFetch<Order>(`/orders/${id}/clone`, { method: "POST" }),
  /**
   * The orders already made from this offer, oldest first. The offer screen reads this before
   * it offers the conversion, so a second order from one offer never happens unnoticed.
   */
  listFromOffer: (offerId: string) => apiFetch<Order[]>(`/orders/from-offer/${offerId}`),
  /** Makes an order out of an offer: it starts as a draft and keeps the link to the offer. */
  createFromOffer: (offerId: string) =>
    apiFetch<Order>(`/orders/from-offer/${offerId}`, { method: "POST" }),
  stats: (params: { year?: number; compareYear?: number; companyId?: string } = {}) =>
    apiFetch<OrderStats>(
      `/orders/stats${q({
        year: params.year?.toString(),
        compareYear: params.compareYear?.toString(),
        companyId: params.companyId,
      })}`,
    ),
};

export type YearStats = {
  year: number;
  total: number;
  monthlyCounts: number[];
  byStatus: Record<string, number>;
  totalQuantity: string; // BigDecimal serialised
};

export type OrderStats = {
  current: YearStats;
  compare: YearStats | null;
};

export function nextAllowed(from: OrderStatus): OrderStatus[] {
  switch (from) {
    case "DRAFT":          return ["SUBMITTED", "CANCELLED"];
    case "SUBMITTED":      return ["IN_REVIEW", "CANCELLED"];
    case "IN_REVIEW":      return ["APPROVED", "REJECTED", "CANCELLED"];
    case "APPROVED":       return ["SHIPPED", "CANCELLED"];
    case "SHIPPED":        return ["INVOICED"];
    default:               return [];
  }
}

/** True when the signed-in user is the one who entered this order. */
export function enteredByViewer(
  order: Pick<Order, "createdBy">,
  viewerId: string | null | undefined,
): boolean {
  return !!viewerId && order.createdBy === viewerId;
}

/**
 * The transitions a screen may offer this viewer: the FSM moves from
 * {@link nextAllowed}, without the approval on an order the viewer entered.
 *
 * Approval is the step where the order enters the ERP, so it carries a second
 * signature: whoever entered the order is not the one who approves it. The
 * server is the authority on that rule and refuses with ORDER_APPROVAL_SELF;
 * the screens ask here so they never offer an action that can only come back
 * refused. Whom the administrator lets order without approval
 * ({@code orders:without_approval}, per role and per user) needs no second
 * signature on their own order.
 *
 * An order carried over from the ERP moves only in the ERP, so the portal offers it no move.
 */
export function nextAllowedFor(
  order: Pick<Order, "status" | "createdBy" | "fromErp">,
  viewerId: string | null | undefined,
  ownWithoutApproval = false,
): OrderStatus[] {
  if (order.fromErp) return [];
  // Shipping and invoicing are the ERP's dispatch note and invoice, not a click in the portal: after the approval the
  // order goes on as a dispatch order, on which quality picks the lot.
  const all = nextAllowed(order.status).filter((to) => to !== "SHIPPED" && to !== "INVOICED");
  if (!enteredByViewer(order, viewerId) || ownWithoutApproval) return all;
  return all.filter((to) => to !== "APPROVED");
}

/**
 * True when the approval is the move being withheld from this viewer, so the
 * screen says so in words instead of leaving a gap where the button would be.
 */
export function approvalWaitsForAnotherSigner(
  order: Pick<Order, "status" | "createdBy" | "fromErp">,
  viewerId: string | null | undefined,
  ownWithoutApproval = false,
): boolean {
  return !ownWithoutApproval && !order.fromErp && enteredByViewer(order, viewerId)
    && nextAllowed(order.status).includes("APPROVED");
}

export function statusBadgePalette(status: OrderStatus): { bg: string; fg: string } {
  switch (status) {
    case "DRAFT":          return { bg: "var(--surface-muted)", fg: "var(--ink-3)" };
    case "SUBMITTED":      return { bg: "var(--info-bg)", fg: "var(--info)" };
    case "IN_REVIEW":      return { bg: "var(--warn-bg)", fg: "var(--warn)" };
    case "APPROVED":       return { bg: "var(--tone-cyan-bg)", fg: "var(--tone-cyan)" };
    case "REJECTED":       return { bg: "var(--err-bg)", fg: "var(--err)" };
    case "SHIPPED":        return { bg: "var(--tone-indigo-bg)", fg: "var(--tone-indigo)" };
    case "INVOICED":       return { bg: "var(--ok-bg)", fg: "var(--ok)" };
    case "CANCELLED":      return { bg: "var(--surface-muted)", fg: "var(--ink-3)" };
  }
  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  return { bg: "var(--surface-2)", fg: "var(--ink-2)" };
}
